// ============================================================
// ByOurConsent.com -- Welcome Page
// Shown after successful registration
// ============================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import Nav    from '../components/Nav';
import useWindowWidth from '../hooks/useWindowWidth';
import Footer from '../components/Footer';

const WelcomePage = () => {
    const { user } = useAuth();
    const width    = useWindowWidth();
    const isMobile = width < 768;
    const firstName = user?.first_name || 'Citizen';

    return (
        <>
            <Helmet><title>Welcome — By Our Consent</title></Helmet>
            <Nav />

            <div style={{ background: '#f5f0e8', minHeight: 'calc(100vh - 56px)' }}>

                {/* Hero welcome band */}
                <div style={{
                    background:     '#1e2a3a',
                    padding:        isMobile ? '1.5rem 1rem' : '3rem 1.5rem',
                    textAlign:      'center',
                    borderBottom:   '2px solid #8b1a1a'
                }}>
                    <div style={{
                        fontSize:      '11px', fontFamily: 'sans-serif',
                        letterSpacing: '0.18em', textTransform: 'uppercase',
                        color:         '#8b6a3a', marginBottom: '0.75rem'
                    }}>
                        Est. 1776 / Reclaimed 2026
                    </div>
                    <h1 style={{
                        fontSize:   '36px', fontWeight: 600,
                        color:      '#e8dfc8', marginBottom: '0.5rem',
                        fontFamily: "'EB Garamond', Georgia, serif"
                    }}>
                        Welcome to the movement, {firstName}.
                    </h1>
                    <p style={{
                        fontSize:   '16px', fontStyle: 'italic',
                        color:      '#8a9aaa', marginBottom: 0,
                        fontFamily: "'EB Garamond', Georgia, serif"
                    }}>
                        You've joined thousands of citizens standing for constitutional governance,
                        accountability, and government by the people.
                    </p>
                </div>

                {/* What you get */}
                <div style={{ maxWidth: '860px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2.5rem 1.5rem' }}>

                    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <div style={{
                            fontSize: '10px', fontFamily: 'sans-serif',
                            letterSpacing: '0.16em', textTransform: 'uppercase',
                            color: '#8b6a3a', marginBottom: '4px'
                        }}>
                            As a member you now have access to
                        </div>
                        <h2 style={{
                            fontSize: '24px', fontWeight: 600, color: '#1e2a3a',
                            fontFamily: "'EB Garamond', Georgia, serif"
                        }}>
                            Everything the platform has to offer
                        </h2>
                        <div style={{ width: '36px', height: '2px', background: '#8b1a1a', margin: '10px auto 0' }} />
                    </div>

                    {/* Benefits grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '14px',
                        marginBottom: '2.5rem'
                    }}>
                        {[
                            {
                                icon: '📰',
                                title: 'Articles & commentary',
                                desc:  'In-depth analysis of constitutional issues, civic history, and government accountability. Non-partisan, factual, well-sourced.',
                                link:  '/articles',
                                cta:   'Read articles'
                            },
                            {
                                icon: '⚡',
                                title: 'Daily constitutional brief',
                                desc:  'What happened today that matters to every citizen. Text summary plus daily audio brief — stay informed in 5 minutes.',
                                link:  '/civic',
                                cta:   'See today\'s brief'
                            },
                            {
                                icon: '✊',
                                title: 'Take action',
                                desc:  'Specific, current action items — who to call, what to say, when to show up. Turn awareness into impact.',
                                link:  '/civic',
                                cta:   'Take action today'
                            },
                            {
                                icon: '🧠',
                                title: 'Weekly quiz',
                                desc:  'Test your constitutional knowledge every week. Leaderboard, streaks, and explanations that actually teach.',
                                link:  '/quiz',
                                cta:   'Take the quiz'
                            },
                            {
                                icon: '🎙️',
                                title: 'Podcast',
                                desc:  'Weekly deep-dive conversations with constitutional lawyers, historians, and civic leaders. Non-partisan, principled.',
                                link:  '/podcast',
                                cta:   'Listen now'
                            },
                            {
                                icon: '👥',
                                title: 'Chapters',
                                desc:  'Join a local or interest-based chapter. Connect with citizens near you, share articles, organize locally.',
                                link:  '/chapters',
                                cta:   'Find a chapter'
                            }
                        ].map((item, i) => (
                            <div key={i} style={{
                                background: '#ede8dc', border: '0.5px solid #c8beaa',
                                borderRadius: '4px', padding: '1.25rem',
                                display: 'flex', flexDirection: 'column', gap: '6px'
                            }}>
                                <div style={{ fontSize: '24px' }}>{item.icon}</div>
                                <div style={{
                                    fontSize: '14px', fontWeight: 600, color: '#1e2a3a',
                                    fontFamily: "'EB Garamond', Georgia, serif"
                                }}>
                                    {item.title}
                                </div>
                                <div style={{
                                    fontSize: '12px', fontFamily: 'sans-serif',
                                    color: '#5a6070', lineHeight: 1.55, flex: 1
                                }}>
                                    {item.desc}
                                </div>
                                <Link to={item.link} style={{
                                    fontSize: '10px', fontFamily: 'sans-serif',
                                    color: '#8b1a1a', letterSpacing: '0.06em',
                                    textTransform: 'uppercase', textDecoration: 'none',
                                    marginTop: '4px'
                                }}>
                                    {item.cta} →
                                </Link>
                            </div>
                        ))}
                    </div>

                    {/* Store callout */}
                    <div style={{
                        background: '#1e2a3a', borderRadius: '4px',
                        padding: isMobile ? '1rem' : '1.5rem 2rem',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', gap: '1.5rem',
                        flexWrap: 'wrap', marginBottom: '2rem'
                    }}>
                        <div>
                            <div style={{
                                fontSize: '18px', fontWeight: 600, color: '#e8dfc8',
                                fontFamily: "'EB Garamond', Georgia, serif",
                                marginBottom: '4px'
                            }}>
                                Wear the movement
                            </div>
                            <div style={{
                                fontSize: '13px', fontFamily: 'sans-serif', color: '#7a8a9a'
                            }}>
                                Every item purchased helps spread the word.
                                Power of the people merchandise — non-partisan, principled.
                            </div>
                        </div>
                        <Link to="/store" style={{
                            background: '#8b1a1a', color: '#f5f0e8',
                            padding: '10px 22px', fontSize: '12px',
                            fontFamily: 'sans-serif', letterSpacing: '0.08em',
                            textTransform: 'uppercase', textDecoration: 'none',
                            borderRadius: '3px', whiteSpace: 'nowrap'
                        }}>
                            Shop the movement
                        </Link>
                    </div>

                    {/* Start here CTA */}
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: '13px', fontFamily: 'sans-serif',
                            color: '#6a7080', marginBottom: '1rem'
                        }}>
                            Not sure where to start?
                        </div>
                        <Link to="/" style={{
                            background: '#8b1a1a', color: '#f5f0e8',
                            padding: '12px 28px', fontSize: '12px',
                            fontFamily: 'sans-serif', letterSpacing: '0.08em',
                            textTransform: 'uppercase', textDecoration: 'none',
                            borderRadius: '3px', display: 'inline-block'
                        }}>
                            Go to the homepage
                        </Link>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default WelcomePage;
